/**
 * Resource Management Utilities for S3 Image Resources
 *
 * This module provides functions to build, merge, and manage resources
 * for question creation and updates.
 */

import {
  parseDataImageSrc,
  replaceImgNodes,
  getImageFormat,
  generateImageFilename,
} from './imageExtraction';

const imgTagPlaceholder = (position) => `{{ img_tag(${position}) }}`;

const imageFromImgNode = (img, parsed) => ({
  base64: parsed.base64,
  format: parsed.format,
  alt: img.getAttribute('alt') || '',
  title: img.getAttribute('title') || '',
});

/**
 * Builds resource objects from extracted base64 images
 * @param {Array} images - Array of image objects with base64, format, etc.
 * @param {number} startPosition - Starting position for numbering (default 0)
 * @returns {Array} Array of resource objects ready for API
 */
export const buildResourcesFromImages = (images, startPosition = 0) => {
  if (!images || images.length === 0) {
    return [];
  }

  return images.map((image, index) => {
    const position = startPosition + index;
    const format = image.format || getImageFormat(image.base64) || 'png';
    const name = generateImageFilename(format, position);

    return {
      raw_bytes: image.base64,
      name: name,
      caption: image.alt || image.title || name,
      position: position,
    };
  });
};

const replaceDataImagesWithResources = (html, startPosition) => {
  const images = [];
  let currentPosition = startPosition;

  const processedHtml = replaceImgNodes(html, (img) => {
    const parsed = parseDataImageSrc(img.getAttribute('src'));
    if (!parsed) {
      return null;
    }

    images.push(imageFromImgNode(img, parsed));
    const placeholder = imgTagPlaceholder(currentPosition);
    currentPosition += 1;
    return placeholder;
  });

  return {
    processedHtml,
    resources: buildResourcesFromImages(images, startPosition),
    endPosition: currentPosition,
  };
};

/**
 * Extracts resources from statement and choices HTML for question creation
 * Returns processed HTML with placeholders and resources array
 * @param {string} statement - Question statement HTML
 * @param {Array} choices - Array of choice objects with text field
 * @returns {{processedStatement: string, processedChoices: Array, resources: Array}}
 */
export const extractResourcesFromContent = (statement, choices = []) => {
  const statementResult = replaceDataImagesWithResources(statement, 0);
  const allResources = [...statementResult.resources];
  let globalPosition = statementResult.endPosition;

  const processedChoices = choices.map((choice) => {
    const choiceResult = replaceDataImagesWithResources(choice.text, globalPosition);
    globalPosition = choiceResult.endPosition;
    allResources.push(...choiceResult.resources);

    return {
      ...choice,
      text: choiceResult.processedHtml,
    };
  });

  return {
    processedStatement: statementResult.processedHtml,
    processedChoices,
    resources: allResources,
  };
};

/**
 * Processes resources for question update
 * Determines which resources to keep, which are new, and which are deleted
 * @param {Array} originalResources - Resources from backend (with id, signed_url, position)
 * @param {string} statement - Current statement HTML from editor
 * @param {Array} choices - Current choices array with text field
 * @returns {{processedStatement: string, processedChoices: Array, resources: Array}}
 */
export const processUpdateResources = (originalResources = [], statement, choices = []) => {
  let globalPosition = 0;
  const finalResources = [];
  const keptResourceIds = new Set();

  const processHtml = (html) =>
    replaceImgNodes(html, (img) => {
      const resourceIdAttr = img.getAttribute('data-resource-id');
      if (resourceIdAttr) {
        const id = parseInt(resourceIdAttr, 10);
        const originalResource = originalResources.find((resource) => resource.id === id);
        if (!originalResource) {
          return null;
        }

        keptResourceIds.add(id);
        const position = globalPosition;
        finalResources.push({
          id: originalResource.id,
          position,
          name: originalResource.name,
          caption: originalResource.caption,
        });
        globalPosition += 1;
        return imgTagPlaceholder(position);
      }

      const parsed = parseDataImageSrc(img.getAttribute('src'));
      if (!parsed) {
        return null;
      }

      const position = globalPosition;
      finalResources.push(...buildResourcesFromImages([imageFromImgNode(img, parsed)], position));
      globalPosition += 1;
      return imgTagPlaceholder(position);
    });

  const processedStatement = processHtml(statement);
  const processedChoices = choices.map((choice) => ({
    ...choice,
    text: processHtml(choice.text),
  }));

  return {
    processedStatement,
    processedChoices,
    resources: finalResources,
    deletedResourceIds: originalResources
      .filter((resource) => !keptResourceIds.has(resource.id))
      .map((resource) => resource.id),
  };
};

/**
 * Hydrates HTML with resources for editing
 * Replaces {{ img_tag(N) }} placeholders with img tags with data-resource-id
 * @param {string} html - HTML with placeholders
 * @param {Array} resources - Array of resources with signed_url, id, position
 * @returns {string} HTML with img tags that have data-resource-id attributes
 */
export const hydrateHtmlWithResources = (html, resources = []) => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  if (!resources || resources.length === 0) {
    return html;
  }

  let result = html;
  const sortedResources = [...resources].sort((a, b) => a.position - b.position);

  sortedResources.forEach((resource) => {
    const placeholder = new RegExp(
      `\\{\\{\\s*img_tag\\(${resource.position}\\)\\s*\\}\\}`
    );
    const altText = resource.caption || resource.name || '';
    const imgTag = `<img src="${resource.signed_url}" data-resource-id="${resource.id}" alt="${altText}" title="${altText}" />`;
    result = result.replace(placeholder, imgTag);
  });

  return result;
};

const isExistingResource = (resource) =>
  typeof resource.id === 'number' && !resource.raw_bytes;

const isNewResource = (resource) =>
  Boolean(resource.raw_bytes) && resource.id == null;

/**
 * Validates resources array structure
 * @param {Array} resources - Resources array to validate
 * @returns {{valid: boolean, errors: Array<string>}}
 */
export const validateResources = (resources) => {
  const errors = [];

  if (!Array.isArray(resources)) {
    errors.push('Resources must be an array');
    return { valid: false, errors };
  }

  const positions = resources.map((resource) => resource.position);
  const uniquePositions = new Set(positions);
  if (positions.length !== uniquePositions.size) {
    errors.push('Resources have duplicate positions');
  }

  const sortedPositions = [...positions].sort((a, b) => a - b);
  for (let i = 0; i < sortedPositions.length; i++) {
    if (sortedPositions[i] !== i) {
      errors.push(
        `Resources positions are not sequential (expected ${i}, found ${sortedPositions[i]})`
      );
      break;
    }
  }

  resources.forEach((resource, index) => {
    if (typeof resource.position !== 'number') {
      errors.push(`Resource at index ${index} missing position`);
    }

    if (isExistingResource(resource)) {
      return;
    }

    if (isNewResource(resource)) {
      return;
    }

    if (typeof resource.id === 'number' && resource.raw_bytes) {
      errors.push(`Resource at index ${index} should not have both id and raw_bytes`);
      return;
    }

    errors.push(
      `Resource at index ${index} must have either id (existing) or raw_bytes (new)`
    );
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Merges resources from statement and choices into a single array
 * Ensures positions are sequential
 * @param {Array} statementResources - Resources from statement
 * @param {Array} choicesResources - Resources from all choices
 * @returns {Array} Merged and renumbered resources
 */
export const mergeResourceArrays = (statementResources = [], choicesResources = []) => {
  const merged = [...statementResources, ...choicesResources];

  return merged.map((resource, index) => ({
    ...resource,
    position: index,
  }));
};

/**
 * Counts resources by type (existing vs new)
 * @param {Array} resources - Resources array
 * @returns {{kept: number, new: number, total: number}}
 */
export const countResourcesByType = (resources = []) => {
  const kept = resources.filter(isExistingResource).length;
  const newCount = resources.filter(isNewResource).length;

  return {
    kept,
    new: newCount,
    total: resources.length,
  };
};
